import type { Vendor } from '../../types';

export interface FocusTarget {
  /** A unique key — when this changes, the map pans/zooms to `coordinates`. */
  key: string;
  coordinates: [number, number];
  labelPosition?: 'left' | 'right' | 'top' | 'bottom';
  /**
   * When present, `MapView` uses `fitBounds` instead of `setView` so that
   * all booths fit on screen. The centroid pan is still used as the visual
   * anchor but the zoom level adjusts to show the full spread.
   * Format: [[south, west], [north, east]] (Leaflet LatLngBounds).
   */
  bounds?: [[number, number], [number, number]];
}

/** Build a FocusTarget from a vendor (key bumps so re-focusing pans again). */
export function focusTargetFor(vendor: Vendor, key?: string): FocusTarget {
  return {
    key: key ?? `${vendor.uid}:${Date.now()}`,
    coordinates: vendor.coordinates,
    labelPosition: vendor.labelPosition,
  };
}

/**
 * Build a FocusTarget at the centroid of all booths for a multi-position
 * vendor. Includes SW/NE bounds so MapView calls `fitBounds` and adjusts
 * zoom to show all markers. Falls back to `focusTargetFor` for a single booth.
 * labelPosition is omitted since the centroid may not align with any marker.
 */
export function centroidFocusTarget(booths: [Vendor, ...Vendor[]]): FocusTarget {
  if (booths.length === 1) return focusTargetFor(booths[0]);
  const uid = booths[0].uid;
  const lats = booths.map((v) => v.coordinates[0]);
  const lngs = booths.map((v) => v.coordinates[1]);
  const lat = lats.reduce((s, v) => s + v, 0) / booths.length;
  const lng = lngs.reduce((s, v) => s + v, 0) / booths.length;
  return {
    key: `${uid}:centroid:${Date.now()}`,
    coordinates: [lat, lng],
    bounds: [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ],
  };
}

/**
 * Pixel offsets applied in Leaflet's projected coordinate space so that the
 * label doesn't overlap the map edge.
 *
 * Positive Y moves the pan centre downward in projected space, which shifts
 * the marker UPWARD on screen (CRS.Simple flips the Y axis).
 */
export const LABEL_OFFSET_PX: Record<string, [number, number]> = {
  right: [45, 0],
  left: [-45, 0],
  bottom: [0, 18],
  top: [0, -18],
};

/**
 * Compute the [x, y] pixel offset to add to the projected marker position
 * before calling `setView`, so the marker lands in the centre of the
 * visible map area.
 *
 * The offset is applied as: `adjusted = project(target) + offset`, then
 * `setView(unproject(adjusted), zoom)`. Because CRS.Simple negates lat when
 * projecting, a positive Y offset shifts the view-centre downward (south),
 * which moves the marker upward on screen — into the centre of the visible
 * area above any bottom sheet.
 *
 * @param labelPosition  Optional label side. Shifts centre away from the
 *                       label so it stays clear of the edge.
 * @param bottomInsetPx  Pixels obscured at the bottom (e.g., a bottom
 *                       sheet). Half this value is added to Y so the marker
 *                       lands in the vertical centre of the visible area
 *                       rather than the full viewport.
 */
export function computeFocusPanOffset(
  labelPosition: string | undefined,
  bottomInsetPx: number,
): [number, number] {
  const [offsetX, offsetY] = LABEL_OFFSET_PX[labelPosition ?? ''] ?? [0, 0];
  const sheetAdjustY = bottomInsetPx > 0 ? Math.round(bottomInsetPx / 2) : 0;
  return [offsetX, offsetY + sheetAdjustY];
}
